/** Smart Purchase Optimizer — domain types (Sprint 1.5) */

export type OptimizationStrategy =
  | 'MENOR_PRECO'
  | 'MENOR_FRETE'
  | 'MENOR_PRAZO'
  | 'MENOR_NUMERO_FORNECEDORES'
  | 'MELHOR_SCORE'
  | 'PERSONALIZADO'

export type FreightMode = 'FIXED' | 'VARIABLE' | 'FREE' | 'PICKUP'

export interface OptimizationWeights {
  price: number
  freight: number
  delivery: number
  supplierCount: number
}

export const DEFAULT_OPTIMIZATION_WEIGHTS: OptimizationWeights = {
  price: 0.45,
  freight: 0.2,
  delivery: 0.2,
  supplierCount: 0.15,
}

export interface SupplierFreightRule {
  supplier: string
  mode: FreightMode
  fixedAmount: number
  variableRate: number
  freeFreightMin: number
  minOrderValue: number
}

export interface OptimizerDemandItem {
  id: string
  productId: string
  description: string
  sku: string
  quantity: number
  currentUnitPrice: number
  brand?: string
  model?: string
  ean?: string
}

export interface SupplierOfferCandidate {
  offerId: string
  productKey: string
  demandId: string
  supplier: string
  description: string
  sku: string
  unitPrice: number
  stock: number
  deliveryDays: number
  brand?: string
  model?: string
}

export interface OptimizationItem {
  product: string
  productId: string
  demandId: string
  supplier: string
  quantity: number
  unitPrice: number
  subtotal: number
  delivery: number
  sku: string
}

export interface OptimizationResult {
  strategy: OptimizationStrategy
  totalCost: number
  productCost: number
  freightCost: number
  supplierCount: number
  estimatedDelivery: number
  economy: number
  economyPercent: number
  score: number
  items: OptimizationItem[]
  freightBySupplier: Array<{
    supplier: string
    productCost: number
    freight: number
    freeFreightApplied: boolean
    mode: FreightMode
    deliveryDays: number
  }>
  warnings: string[]
}

export interface PlanComparison {
  current: {
    productCost: number
    freightCost: number
    totalCost: number
    supplierCount: number
    estimatedDelivery: number
  }
  optimized: {
    productCost: number
    freightCost: number
    totalCost: number
    supplierCount: number
    estimatedDelivery: number
  }
  economy: number
  economyPercent: number
  deliveryDiff: number
}

export interface OptimizationHistoryEntry {
  id: string
  quoteId: string
  strategy: OptimizationStrategy
  economy: number
  totalCost: number
  supplierCount: number
  createdAt: string
}

export interface OptimizerConfig {
  weights: OptimizationWeights
}

export const STRATEGY_LABELS: Record<OptimizationStrategy, string> = {
  MENOR_PRECO: 'Menor preço',
  MENOR_FRETE: 'Menor frete',
  MENOR_PRAZO: 'Menor prazo',
  MENOR_NUMERO_FORNECEDORES: 'Menor nº de fornecedores',
  MELHOR_SCORE: 'Melhor score',
  PERSONALIZADO: 'Personalizado',
}
